import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, Flame, HeartPulse, Plus, Save, ShieldAlert, Siren, Trash2 } from "lucide-react";
import { apiRequest } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const emptyResponder = { full_name: "", role: "", phone: "", training_certificate: "", training_expiry: "" };

function ResponderList({ title, icon: Icon, list, onChange, testIdPrefix, canEdit }) {
  const update = (idx, field, value) => {
    const next = [...list];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };
  const add = () => onChange([...list, { ...emptyResponder }]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  return (
    <Card className="shadow-none" data-testid={`${testIdPrefix}-card`}>
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Icon className="h-5 w-5 text-blue-900" /> {title} <Badge className="bg-slate-100 text-slate-700" data-testid={`${testIdPrefix}-count`}>{list.length}</Badge></CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500" data-testid={`${testIdPrefix}-empty`}>Nessun addetto designato.</p>}
        {list.map((r, idx) => (
          <div key={idx} className="grid gap-2 rounded-md border border-slate-200 p-3 md:grid-cols-6" data-testid={`${testIdPrefix}-row-${idx}`}>
            <Input placeholder="Nome e cognome" value={r.full_name || ""} onChange={(e) => update(idx, "full_name", e.target.value)} disabled={!canEdit} data-testid={`${testIdPrefix}-name-${idx}`} />
            <Input placeholder="Ruolo" value={r.role || ""} onChange={(e) => update(idx, "role", e.target.value)} disabled={!canEdit} data-testid={`${testIdPrefix}-role-${idx}`} />
            <Input placeholder="Telefono" value={r.phone || ""} onChange={(e) => update(idx, "phone", e.target.value)} disabled={!canEdit} data-testid={`${testIdPrefix}-phone-${idx}`} />
            <Input placeholder="Certificato" value={r.training_certificate || ""} onChange={(e) => update(idx, "training_certificate", e.target.value)} disabled={!canEdit} data-testid={`${testIdPrefix}-cert-${idx}`} />
            <Input type="date" value={r.training_expiry || ""} onChange={(e) => update(idx, "training_expiry", e.target.value)} disabled={!canEdit} data-testid={`${testIdPrefix}-expiry-${idx}`} />
            {canEdit && <Button variant="outline" size="sm" className="text-red-700" onClick={() => remove(idx)} data-testid={`${testIdPrefix}-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
        {canEdit && <Button variant="outline" size="sm" onClick={add} data-testid={`${testIdPrefix}-add`}><Plus className="h-4 w-4" /> Aggiungi addetto</Button>}
      </CardContent>
    </Card>
  );
}

export default function EmergencyPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [saving, setSaving] = useState(false);
  const canEdit = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"].includes(user.role);

  const load = async () => {
    if (!id) return;
    setError("");
    try {
      const [companyData, planData] = await Promise.all([
        apiRequest(`/companies/${id}`),
        apiRequest(`/companies/${id}/emergency-plan`),
      ]);
      setCompany(companyData);
      setPlan(planData);
    } catch (err) { setError(err.message); }
  };
  useEffect(() => { load(); }, [id]);

  if (!company || !plan) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="emergency-loading">Caricamento piano emergenze...</div>;

  const update = (field, value) => setPlan({ ...plan, [field]: value });

  const submit = async () => {
    setSaving(true); setError(""); setInfo("");
    try {
      const payload = {
        datore_lavoro: plan.datore_lavoro || "",
        rspp_name: plan.rspp_name || "",
        addetti_antincendio: (plan.addetti_antincendio || []).filter((r) => r.full_name && r.full_name.length >= 2),
        addetti_primo_soccorso: (plan.addetti_primo_soccorso || []).filter((r) => r.full_name && r.full_name.length >= 2),
        piano_evacuazione_descrizione: plan.piano_evacuazione_descrizione || "",
        vie_esodo_descrizione: plan.vie_esodo_descrizione || "",
        luoghi_raccolta: plan.luoghi_raccolta || "",
        numeri_emergenza: plan.numeri_emergenza || "",
        presidio_antincendio: plan.presidio_antincendio || "",
        cassetta_primo_soccorso: !!plan.cassetta_primo_soccorso,
        defibrillatore_disponibile: !!plan.defibrillatore_disponibile,
        last_drill_date: plan.last_drill_date || "",
        notes: plan.notes || "",
      };
      const result = await apiRequest(`/companies/${id}/emergency-plan`, { method: "PUT", body: JSON.stringify(payload) });
      setPlan(result);
      setInfo(`Piano emergenze salvato. Completamento ${result.completion_percentage}%.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="emergency-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="emergency-back-button"><ArrowLeft className="h-4 w-4" /> Scheda azienda</Button>
        <Badge className="bg-blue-50 text-blue-900" data-testid="emergency-company-badge">{company.business_name}</Badge>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="emergency-header">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Gestione emergenze</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Piano emergenze</h2>
        <p className="mt-2 text-slate-600">Designazioni addetti antincendio e primo soccorso, vie di esodo, presidi e numeri utili ai sensi dell&apos;art. 18 e 43 del D.Lgs. 81/2008.</p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-1 flex justify-between text-sm"><span>Completamento piano</span><strong data-testid="emergency-completion-pct">{plan.completion_percentage}%</strong></div>
            <Progress value={plan.completion_percentage} className="h-3" data-testid="emergency-completion-progress" />
          </div>
          <div className="flex gap-2 md:justify-end">
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={submit} disabled={saving || !canEdit} data-testid="emergency-save-button"><Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva piano"}</Button>
          </div>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="emergency-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}
      {info && <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900" data-testid="emergency-info"><CheckCircle2 className="h-4 w-4" /><AlertTitle>Salvataggio completato</AlertTitle><AlertDescription>{info}</AlertDescription></Alert>}

      <Card className="shadow-none" data-testid="emergency-org-card">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Siren className="h-5 w-5 text-blue-900" /> Organizzazione emergenze</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2"><Label>Datore di lavoro</Label><Input value={plan.datore_lavoro || ""} onChange={(e) => update("datore_lavoro", e.target.value)} disabled={!canEdit} data-testid="emergency-dl-input" /></div>
          <div className="space-y-2"><Label>RSPP designato</Label><Input value={plan.rspp_name || ""} onChange={(e) => update("rspp_name", e.target.value)} disabled={!canEdit} data-testid="emergency-rspp-input" /></div>
          <div className="space-y-2"><Label>Numeri di emergenza interni</Label><Input value={plan.numeri_emergenza || ""} onChange={(e) => update("numeri_emergenza", e.target.value)} placeholder="Es. 112 / centralino interno 9000" disabled={!canEdit} data-testid="emergency-numeri-input" /></div>
          <div className="space-y-2"><Label>Presidio antincendio</Label><Input value={plan.presidio_antincendio || ""} onChange={(e) => update("presidio_antincendio", e.target.value)} placeholder="Es. estintori, idranti, naspi" disabled={!canEdit} data-testid="emergency-presidio-input" /></div>
        </CardContent>
      </Card>

      <ResponderList title="Addetti antincendio" icon={Flame} list={plan.addetti_antincendio || []} onChange={(v) => update("addetti_antincendio", v)} testIdPrefix="emergency-antincendio" canEdit={canEdit} />
      <ResponderList title="Addetti primo soccorso" icon={HeartPulse} list={plan.addetti_primo_soccorso || []} onChange={(v) => update("addetti_primo_soccorso", v)} testIdPrefix="emergency-primosoccorso" canEdit={canEdit} />

      <Card className="shadow-none" data-testid="emergency-evac-card">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-blue-900" /> Piano evacuazione</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2"><Label>Descrizione piano evacuazione</Label><Textarea rows={4} value={plan.piano_evacuazione_descrizione || ""} onChange={(e) => update("piano_evacuazione_descrizione", e.target.value)} placeholder="Procedura di allarme, segnalazione, evacuazione, gestione del personale e dei visitatori." disabled={!canEdit} data-testid="emergency-piano-evacuazione" /></div>
          <div className="space-y-2"><Label>Vie di esodo</Label><Textarea rows={3} value={plan.vie_esodo_descrizione || ""} onChange={(e) => update("vie_esodo_descrizione", e.target.value)} placeholder="Descrizione delle vie e uscite di emergenza, segnaletica, illuminazione." disabled={!canEdit} data-testid="emergency-vie-esodo" /></div>
          <div className="space-y-2"><Label>Luoghi di raccolta</Label><Textarea rows={2} value={plan.luoghi_raccolta || ""} onChange={(e) => update("luoghi_raccolta", e.target.value)} placeholder="Punti di raccolta esterni dopo l'evacuazione." disabled={!canEdit} data-testid="emergency-luoghi-raccolta" /></div>
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="emergency-presidio-card">
        <CardHeader><CardTitle className="font-heading flex items-center gap-2"><HeartPulse className="h-5 w-5 text-blue-900" /> Presidi sanitari ed esercitazioni</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <label className="flex items-center gap-2 rounded-md border border-slate-200 p-3 text-sm" data-testid="emergency-cassetta-label"><input type="checkbox" checked={!!plan.cassetta_primo_soccorso} onChange={(e) => update("cassetta_primo_soccorso", e.target.checked)} disabled={!canEdit} data-testid="emergency-cassetta-checkbox" /> Cassetta primo soccorso disponibile</label>
          <label className="flex items-center gap-2 rounded-md border border-slate-200 p-3 text-sm" data-testid="emergency-defibrillatore-label"><input type="checkbox" checked={!!plan.defibrillatore_disponibile} onChange={(e) => update("defibrillatore_disponibile", e.target.checked)} disabled={!canEdit} data-testid="emergency-defibrillatore-checkbox" /> Defibrillatore (DAE) disponibile</label>
          <div className="space-y-2"><Label>Data ultima esercitazione</Label><Input type="date" value={plan.last_drill_date || ""} onChange={(e) => update("last_drill_date", e.target.value)} disabled={!canEdit} data-testid="emergency-last-drill" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Note operative</Label><Textarea rows={2} value={plan.notes || ""} onChange={(e) => update("notes", e.target.value)} placeholder="Note interne, accordi con vigili del fuoco, contratti di manutenzione presidi, ecc." disabled={!canEdit} data-testid="emergency-notes" /></div>
        </CardContent>
      </Card>

      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={submit} disabled={saving} data-testid="emergency-save-bottom-button"><Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva piano"}</Button>
        </div>
      )}
    </div>
  );
}
